import * as React from 'react';
import { Container, Form, Button } from 'semantic-ui-react';
import ReCAPTCHA from 'react-google-recaptcha';
import * as Sentry from '@sentry/browser';
import { recaptchaSize } from '../../helpers/common';
import { submitFormData } from '../../helpers/form-api';

const recaptchaKey = process.env.GATSBY_GOOGLE_RECAPTCHA_KEY || '';

class TechnicalStandardsAndWorkPracticesForm extends React.Component {
  initialFormData = {
    company_name: '',
    company_street_address: '',
    company_suburb: '',
    company_postcode: '',
    company_phone: '',
    first_name: '',
    middle_name: '',
    last_name: '',
    job_role: '',
    phone: '',
    mobile_phone: '',
    email: '',
    contract_category: '',
    contract_id: '',
    contract_start_date: '',
    contract_end_date: '',
    cppc_responsible_officer: '',
    principal_contractor: '',
    reason_for_access: '',
  };

  state = {
    error: null,
    success: null,
    submitting: false,
    formData: {
      ...this.initialFormData,
    },
  };

  recaptchaRef = React.createRef();

  handleCaptcha = (value) => {
    this.setState({ captcha: value });
  };

  handleInputChange = (event) => {
    const target = event.currentTarget;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    const name = target.name;

    this.setState((prevState) => ({
      ...prevState,
      formData: {
        ...prevState.formData,
        [name]: value,
      },
    }));
  };

  handleSelectChange = (event, { name, value }) => {
    this.setState((prevState) => ({
      ...prevState,
      formData: {
        ...prevState.formData,
        [name]: value,
      },
    }));
  };

  handleSubmit = async () => {
    try {
      this.setState({ success: null, error: null, submitting: true });

      if (this.state.captcha) {
        const formData = {
          ...this.state.formData,
          captchaResponse: this.state.captcha,
        };
        const response = await submitFormData(
          'technical_standards_and_work_practices',
          formData
        );

        if (response.status !== 200) {
          this.setState({
            success: null,
            error: response.message,
            submitting: false,
            captcha: null,
          });
        } else {
          this.setState({
            success: 'Success',
            error: null,
            submitting: false,
            formData: { ...this.initialFormData },
            captcha: null,
          });
        }

        if (this.recaptchaRef.current) {
          this.recaptchaRef.current.reset();
        }
      } else {
        this.setState({
          success: null,
          error: 'Please tick ReCAPTCHA',
          submitting: false,
        });
      }
    } catch (err) {
      console.error(err);
      Sentry.captureException(err);
      this.setState({ error: 'Error', success: null, submitting: false });
    }
  };

  submitText = () => {
    if (this.state.submitting) {
      return 'Submitting...';
    } else if (this.state.success) {
      return 'Submitted';
    }
    return 'Submit';
  };

  render() {
    const {
      company_name,
      company_street_address,
      company_suburb,
      company_postcode,
      company_phone,
      first_name,
      middle_name,
      last_name,
      job_role,
      phone,
      mobile_phone,
      email,
      contract_category,
      contract_id,
      contract_start_date,
      contract_end_date,
      cppc_responsible_officer,
      principal_contractor,
      reason_for_access,
    } = this.state.formData;

    const contractCategoryOptions = [
      {
        text: 'Project Manager or Principle Contractor',
        value: 'Project Manager or Principle Contractor',
        key: 'Project Manager or Principle Contractor',
      },
      {
        text: 'Design Contractor',
        value: 'Design Contractor',
        key: 'Design Contractor',
      },
      {
        text: 'Construction Contractor',
        value: 'Construction Contractor',
        key: 'Construction Contractor',
      },
      {
        text: 'Option 2 Contractor',
        value: 'Option 2 Contractor',
        key: 'Option 2 Contractor',
      },
      {
        text: 'Subcontractor (provide details below)',
        value: 'Subcontractor (provide details below)',
        key: 'Subcontractor (provide details below)',
      },
      {
        text: 'Other (specify below)',
        value: 'Other (specify below)',
        key: 'Other (specify below)',
      },
    ];

    return (
      <section>
        <Container>
          <div className="grid-wrapper">
            <div className="form-container">
              <Form onSubmit={this.handleSubmit}>
                <h4>Company details</h4>

                <Form.Input
                  label="Company name"
                  required
                  name="company_name"
                  type="text"
                  value={company_name}
                  onChange={this.handleInputChange}
                  id="form-company-name"
                />

                <Form.Input
                  label="Company street address"
                  required
                  type="text"
                  name="company_street_address"
                  value={company_street_address}
                  onChange={this.handleInputChange}
                  id="form-company-street-address"
                />

                <Form.Input
                  label="Company suburb"
                  required
                  type="text"
                  name="company_suburb"
                  value={company_suburb}
                  onChange={this.handleInputChange}
                  id="form-company-suburb"
                />

                <Form.Input
                  label="Company Postcode"
                  type="text"
                  required
                  name="company_postcode"
                  value={company_postcode}
                  onChange={this.handleInputChange}
                  id="form-company-postcode"
                />

                <Form.Input
                  label="Company phone number"
                  type="text"
                  required
                  name="company_phone"
                  value={company_phone}
                  onChange={this.handleInputChange}
                  id="form-company-phone"
                />

                <h4>Company representative</h4>
                <Form.Input
                  label="First Name"
                  required
                  type="text"
                  name="first_name"
                  value={first_name}
                  onChange={this.handleInputChange}
                  id="form-first-name"
                />

                <Form.Input
                  label="Middle Name"
                  type="text"
                  name="middle_name"
                  value={middle_name}
                  onChange={this.handleInputChange}
                  id="form-middle-name"
                />

                <Form.Input
                  label="Last Name"
                  type="text"
                  name="last_name"
                  required
                  value={last_name}
                  onChange={this.handleInputChange}
                  id="form-last-name"
                />

                <Form.Input
                  label="Job Role"
                  type="text"
                  required
                  name="job_role"
                  value={job_role}
                  onChange={this.handleInputChange}
                  id="form-job-role"
                />

                <Form.Input
                  label="Phone number"
                  type="text"
                  name="phone"
                  value={phone}
                  onChange={this.handleInputChange}
                  id="form-phone"
                />

                <Form.Input
                  label="Mobile number"
                  type="phone"
                  name="mobile_phone"
                  value={mobile_phone}
                  onChange={this.handleInputChange}
                  id="form-mobile-phone"
                />

                <Form.Input
                  label="Email address"
                  type="email"
                  required
                  name="email"
                  value={email}
                  onChange={this.handleInputChange}
                  id="form-email"
                />

                <h4>Contract details</h4>

                <Form.Select
                  label="Contract Category"
                  required
                  options={contractCategoryOptions}
                  value={contract_category}
                  name="contract_category"
                  onChange={this.handleSelectChange}
                  placeholder="Please choose"
                  id="form-contract-category"
                />

                <Form.Input
                  label="Contract ID"
                  type="text"
                  name="contract_id"
                  value={contract_id}
                  onChange={this.handleInputChange}
                  id="form-contract-id"
                />

                <Form.Input
                  className="calendar-icon"
                  label="Contract start date"
                  type="date"
                  name="contract_start_date"
                  value={contract_start_date}
                  onChange={this.handleInputChange}
                  id="form-contract-start-date"
                />

                <Form.Input
                  className="calendar-icon"
                  label="Contract end date"
                  type="date"
                  name="contract_end_date"
                  value={contract_end_date}
                  onChange={this.handleInputChange}
                  id="form-contract-end-date"
                />

                <Form.Input
                  label="CitiPower/Powercor Responsible Officer or Principle Contractor"
                  type="text"
                  name="cppc_responsible_officer"
                  value={cppc_responsible_officer}
                  required
                  onChange={this.handleInputChange}
                  id="form-cppc-responsible-officer"
                />

                <Form.Input
                  label="If working for a principle contractor please provide their contact details."
                  type="text"
                  name="principal_contractor"
                  value={principal_contractor}
                  onChange={this.handleInputChange}
                  id="form-principal-contractor"
                />

                <Form.TextArea
                  label="Reason for requiring access to the technical standards site *"
                  name="reason_for_access"
                  value={reason_for_access}
                  onChange={this.handleInputChange}
                  id="form-reason-for-access"
                />

                <ReCAPTCHA
                  ref={this.recaptchaRef}
                  sitekey={recaptchaKey}
                  onChange={this.handleCaptcha}
                  size={recaptchaSize()}
                />

                <Button
                  className="form--submit-btn"
                  disabled={this.state.submitting}
                  loading={this.state.submitting}
                >
                  {this.submitText()}
                </Button>

                {this.state.success ? (
                  <div className="success-message">{this.state.success}</div>
                ) : null}
                {this.state.error ? (
                  <div className="error-message">{this.state.error}</div>
                ) : null}
              </Form>
            </div>
          </div>
        </Container>
      </section>
    );
  }
}

export default TechnicalStandardsAndWorkPracticesForm;
